import styled from '@emotion/styled'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { MenuItem } from 'app/containers/Footer/MenuItem'
import { styles } from 'app/theme'
import { Media } from 'app/theme/media'
import { useVocabularyData } from 'app/utils/vocabulary'
import { compact, uniqBy } from 'lodash'
import React, { memo } from 'react'

export interface MenuProps {
  languageCode?: string
  title?: string
  URL?: string
}

export interface Props {
  facebookURL?: string
  instagramURL?: string
  languageCode: string
  linkedinURL?: string
  specialsItems?: MenuProps[]
  newsItems?: MenuProps[]
  tiktokURL?: string
}

export const Menu = memo(function Menu({
  facebookURL,
  instagramURL,
  languageCode,
  linkedinURL,
  specialsItems,
  newsItems,
  tiktokURL,
}: Props) {
  if (!newsItems && !specialsItems) {
    return null
  }

  return (
    <Container row space="between" wrap>
      <Media className="desktop_media" greaterThanOrEqual="desktopSmall">
        {specialsItems && specialsItems?.length > 0 ? (
          <Box>
            <Label>{useVocabularyData('specials', languageCode)}</Label>

            <Nav>
              {uniqBy(specialsItems, 'title').map((item: any, index) => (
                <Li key={index}>
                  <MenuItem {...item} />
                </Li>
              ))}
            </Nav>
          </Box>
        ) : null}

        {newsItems && newsItems?.length > 0 ? (
          <Box>
            <Label>{useVocabularyData('news', languageCode)}</Label>

            <Nav>
              {uniqBy(newsItems, 'title').map((item: any, index) => (
                <Li key={index}>
                  <MenuItem {...item} />
                </Li>
              ))}
            </Nav>
          </Box>
        ) : null}
      </Media>

      <Media className="mobile_media" lessThan="desktopSmall">
        <MobileWrapper row>
          {specialsItems && specialsItems?.length > 0 ? (
            <Box>
              <Label>{useVocabularyData('specials', languageCode)}</Label>

              <Nav>
                {uniqBy(specialsItems, 'title').map((item: any, index) => (
                  <Li key={index}>
                    <MenuItem {...item} />
                  </Li>
                ))}
              </Nav>
            </Box>
          ) : null}

          {newsItems && newsItems?.length > 0 ? (
            <Box>
              <Label>{useVocabularyData('news', languageCode)}</Label>

              <Nav>
                {uniqBy(newsItems, 'title').map((item: any, index) => (
                  <Li key={index}>
                    <MenuItem {...item} />
                  </Li>
                ))}
              </Nav>
            </Box>
          ) : null}
        </MobileWrapper>
      </Media>

      <SocialBox>
        <Label>{useVocabularyData('social', languageCode)}</Label>

        <Wrapper>
          {facebookURL ? (
            <Anchor
              aria-label="Facebook"
              href={facebookURL}
              rel="noreferrer"
              target="_blank"
            >
              Facebook
            </Anchor>
          ) : null}

          {instagramURL ? (
            <Anchor
              aria-label="Instagram"
              href={instagramURL}
              rel="noreferrer"
              target="_blank"
            >
              Instagram
            </Anchor>
          ) : null}

          {tiktokURL ? (
            <Anchor
              aria-label="Tiktok"
              href={tiktokURL}
              rel="noreferrer"
              target="_blank"
            >
              Tiktok
            </Anchor>
          ) : null}

          {linkedinURL ? (
            <Anchor
              aria-label="Linkedin"
              href={linkedinURL}
              rel="noreferrer"
              target="_blank"
            >
              Linkedin
            </Anchor>
          ) : null}
        </Wrapper>
      </SocialBox>
    </Container>
  )
})

const Container = styled(FlexBox)`
  .desktop_media {
    display: contents;
  }

  @media (max-width: 1199px) {
    width: 100%;

    .mobile_media {
      width: 100%;
    }
  }
`

const Box = styled.div`
  ${styles.description.light}

  max-width: 5.625rem;
  margin-left: 9.6875rem;

  &:first-of-type {
    margin-left: 0;
  }

  @media (max-width: 1199px) {
    margin-left: 0;
  }
`

const SocialBox = styled.div`
  ${styles.description.light}

  width: 4.125rem;
  margin-left: 9.6875rem;

  @media (max-width: 1199px) {
    width: 100%;
    margin-left: 0;
    padding: 3rem;
    border-top: 0.0625rem solid
      ${({ theme }) => theme.colors.variants.primaryLight};
  }
`

const MobileWrapper = styled(FlexBox)`
  border-top: 0.0625rem solid
    ${({ theme }) => theme.colors.variants.primaryLight};
  width: 100%;
  column-gap: 7.8125rem;
  padding: 3rem;
`

const Wrapper = styled.div`
  @media (max-width: 1199px) {
    display: flex;
    flex-wrap: wrap;
    column-gap: 7.8125rem;
  }
`

const Label = styled(FlexBox)`
  ${styles.label}

  margin-bottom: 1.25rem;

  @media (max-width: 1199px) {
    opacity: 0.5;
    justify-content: center;
    margin-bottom: 1.5rem;

    &::before {
      display: none;
    }
  }
`

const Anchor = styled.a`
  display: inline-block;
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);
  margin-bottom: 0.75rem;

  &:last-of-type {
    margin-bottom: 0;
  }

  &:hover {
    transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);
    opacity: 0.5;
  }

  @media (max-width: 1199px) {
    width: 4.125rem;

    :nth-last-of-type(-n + 2) {
      margin-bottom: 0;
    }
  }
`

const Nav = styled.ul``

const Li = styled.li`
  margin-top: 0.5rem;
`
